import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle,
  Cloud,
  Database,
  HardDrive,
  Loader2,
  RefreshCw,
  Server,
  Upload,
  XCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DatabaseMigration = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentJob, setCurrentJob] = useState(null);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [clearExisting, setClearExisting] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/database/stats`);
      setStats(res.data);
      
      // Auto-select all collections
      if (res.data?.local?.collections) {
        setSelectedCollections(res.data.local.collections.map(c => c.name));
      }
    } catch (error) {
      toast.error("Failed to fetch database stats");
    }
    setLoading(false);
  }, []);

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const res = await axios.get(`${API}/admin/database/migrate/${jobId}`);
      setCurrentJob(res.data);
      return res.data;
    } catch (error) {
      console.error("Failed to poll job status:", error);
      return null;
    }
  }, []);

  const startMigration = async () => {
    if (selectedCollections.length === 0) {
      toast.error("Please select at least one collection");
      return;
    }

    setIsMigrating(true);
    try {
      const res = await axios.post(`${API}/admin/database/migrate`, {
        collections: selectedCollections,
        clear_existing: clearExisting,
      });
      
      toast.success("Migration started");
      setCurrentJob({ job_id: res.data.job_id, status: "starting" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start migration");
      setIsMigrating(false);
    }
  };

  // Polling effect
  useEffect(() => {
    if (!currentJob?.job_id) return;
    
    if (["completed", "failed"].includes(currentJob.status)) {
      setIsMigrating(false);
      fetchStats(); // Refresh stats after completion
      return;
    }

    const interval = setInterval(async () => {
      const job = await pollJobStatus(currentJob.job_id);
      if (job && ["completed", "failed"].includes(job.status)) {
        setIsMigrating(false);
        fetchStats();
        clearInterval(interval);
        
        if (job.status === "completed") {
          toast.success(`Migration completed! ${job.documents_migrated} documents migrated.`);
        } else {
          toast.error(`Migration failed: ${job.error}`);
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentJob?.job_id, currentJob?.status, pollJobStatus, fetchStats]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const toggleCollection = (name) => {
    setSelectedCollections(prev =>
      prev.includes(name)
        ? prev.filter(c => c !== name)
        : [...prev, name]
    );
  };

  const toggleAll = () => {
    if (!stats?.local?.collections) return;
    
    if (selectedCollections.length === stats.local.collections.length) {
      setSelectedCollections([]);
    } else {
      setSelectedCollections(stats.local.collections.map(c => c.name));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="database-migration">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Database Migration</h2>
          <p className="text-gray-600">Migrate data from local to production MongoDB</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchStats}
          disabled={isMigrating}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Stats
        </Button>
      </div>

      {/* Connection Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Local Database */}
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <HardDrive className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Local Database</h3>
              <p className="text-sm text-gray-500">Source</p>
            </div>
            <Badge className="ml-auto bg-green-100 text-green-700">Connected</Badge>
          </div>
          <div className="text-3xl font-bold text-gray-800 mb-1">
            {stats?.local?.total_documents?.toLocaleString() || 0}
          </div>
          <p className="text-gray-600 text-sm">
            {stats?.local?.collections?.length || 0} collections
          </p>
        </div>

        {/* Production Database */}
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Cloud className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Production Database</h3>
              <p className="text-sm text-gray-500">{stats?.production_db_name || "Not configured"}</p>
            </div>
            {stats?.production?.connected ? (
              <Badge className="ml-auto bg-green-100 text-green-700">Connected</Badge>
            ) : stats?.production_configured ? (
              <Badge className="ml-auto bg-amber-100 text-amber-700">
                {stats?.production?.error ? "Error" : "Checking..."}
              </Badge>
            ) : (
              <Badge className="ml-auto bg-gray-100 text-gray-600">Not Configured</Badge>
            )}
          </div>
          <div className="text-3xl font-bold text-gray-800 mb-1">
            {stats?.production?.total_documents?.toLocaleString() || 0}
          </div>
          <p className="text-gray-600 text-sm">
            {stats?.production?.collections?.length || 0} collections
          </p>
          {stats?.production?.error && (
            <p className="text-red-500 text-xs mt-2">{stats.production.error}</p>
          )}
        </div>
      </div>

      {/* Migration Progress */}
      {currentJob && !["completed", "failed"].includes(currentJob.status) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <div>
                <h4 className="font-semibold text-blue-800">Migration in Progress</h4>
                <p className="text-sm text-blue-600">
                  {currentJob.status === "connecting" && "Connecting to production database..."}
                  {currentJob.status === "migrating" && `Migrating ${currentJob.current_collection}...`}
                  {currentJob.status === "creating_indexes" && "Creating indexes..."}
                </p>
              </div>
            </div>
            <span className="text-2xl font-bold text-blue-700">
              {currentJob.progress_percent || 0}%
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${currentJob.progress_percent || 0}%` }}
            />
          </div>
          <div className="mt-3 text-sm text-blue-700">
            {currentJob.collections_migrated}/{currentJob.total_collections} collections • 
            {currentJob.documents_migrated?.toLocaleString()} documents migrated
          </div>
        </div>
      )}

      {/* Migration Result */}
      {currentJob?.status === "completed" && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <h4 className="font-semibold text-green-800">Migration Completed Successfully</h4>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-700">
                {currentJob.collections_migrated}
              </div>
              <div className="text-sm text-green-600">Collections</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-700">
                {currentJob.documents_migrated?.toLocaleString()}
              </div>
              <div className="text-sm text-green-600">Documents Migrated</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-700">
                {currentJob.results?.reduce((acc, r) => acc + r.skipped, 0) || 0}
              </div>
              <div className="text-sm text-green-600">Skipped (Duplicates)</div>
            </div>
          </div>
        </div>
      )}

      {currentJob?.status === "failed" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-600" />
            <div>
              <h4 className="font-semibold text-red-800">Migration Failed</h4>
              <p className="text-sm text-red-600">{currentJob.error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Collections Selection */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold">Collections to Migrate</h3>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <Checkbox
                checked={clearExisting}
                onCheckedChange={setClearExisting}
              />
              Clear existing data in production
            </label>
            <Button variant="outline" size="sm" onClick={toggleAll}>
              {selectedCollections.length === stats?.local?.collections?.length
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats?.local?.collections?.map((coll) => {
              const prodColl = stats?.production?.collections?.find(c => c.name === coll.name);
              const isSelected = selectedCollections.includes(coll.name);
              
              return (
                <div
                  key={coll.name}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => toggleCollection(coll.name)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={isSelected} />
                      <span className="font-medium text-gray-800">{coll.name}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-gray-600">{coll.count.toLocaleString()} docs</span>
                    {prodColl && (
                      <span className="text-purple-600">
                        Prod: {prodColl.count.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Start Migration Button */}
      <div className="flex justify-end">
        <Button
          onClick={startMigration}
          disabled={isMigrating || selectedCollections.length === 0 || !stats?.production_configured}
          className="bg-purple-600 hover:bg-purple-700"
          size="lg"
          data-testid="start-migration-btn"
        >
          {isMigrating ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Upload className="w-5 h-5 mr-2" />
          )}
          Migrate {selectedCollections.length} Collections to Production
        </Button>
      </div>

      {!stats?.production_configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertCircle className="w-5 h-5" />
            <span>Production MongoDB not configured. Set PROD_MONGO_URL and PROD_DB_NAME environment variables.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseMigration;
